import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { cn } from "@/shared/lib/utils";
import type { UploadMedicationGroup } from "@/features/patient/medications/hooks/useMedicationMatching";
import type { MedicationMatch, MedicationMatchLevel } from "@/portals/patient/api/medicationMatchingApi";

// L0-L4 — see medication_normalization/cascade.py's module docstring for
// the full definition of each level; these are the plain-language versions.
const MATCH_LEVEL_LABELS: Record<Exclude<MedicationMatchLevel, null>, string> = {
  L0: "From your provider's coded record",
  L1: "Matched by NDC number",
  L2: "Exact name match",
  L3: "Matched after expanding abbreviations",
  L4: "Approximate / fuzzy match",
};

const STATUS_META: Record<string, { label: string; icon: string; className: string }> = {
  pending: { label: "Matching…", icon: "lucide:loader-2", className: "bg-[#F3F4F6] text-[#6B7280]" },
  accept: { label: "Matched", icon: "lucide:check-circle-2", className: "bg-[#DCFCE7] text-[#16A34A]" },
  review: { label: "Needs review", icon: "lucide:alert-triangle", className: "bg-[#FFFBEB] text-[#B45309]" },
  reject: { label: "No match found", icon: "lucide:x-circle", className: "bg-[#FEF2F2] text-[#DC2626]" },
  failed: { label: "Couldn't process", icon: "lucide:alert-circle", className: "bg-[#FEF2F2] text-[#DC2626]" },
  skipped: { label: "Not processed", icon: "lucide:minus-circle", className: "bg-[#F3F4F6] text-[#6B7280]" },
};

/** Which STATUS_META entry applies — resolved rows key off their confidence
 *  band (accept/review/reject), everything else keys off normalizationStatus
 *  itself (pending/failed/skipped). */
function statusKeyFor(med: MedicationMatch): string {
  if (med.normalizationStatus === "resolved") return med.confidenceBand ?? "reject";
  return med.normalizationStatus;
}

function MedicationStatusBadge({ med }: { med: MedicationMatch }) {
  const meta = STATUS_META[statusKeyFor(med)];
  const isPending = med.normalizationStatus === "pending";
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold", meta.className)}>
      <Icon icon={meta.icon} className={cn("h-3 w-3", isPending && "animate-spin")} />
      {meta.label}
    </span>
  );
}

/** One medication's resolution — original text, what it was matched using,
 *  which cascade level applied (and its raw value), and the result. */
function MedicationRow({ med }: { med: MedicationMatch }) {
  const showCleanedText = med.cleanedMedicationText && med.cleanedMedicationText !== med.medicationName;
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 flex-1 text-sm font-extrabold text-[#0A2947]">{med.medicationName}</p>
        <MedicationStatusBadge med={med} />
      </div>

      {showCleanedText && (
        <p className="mt-1 text-xs text-[#9CA3AF]">Matched using: &ldquo;{med.cleanedMedicationText}&rdquo;</p>
      )}

      {med.matchLevel && (
        <p className="mt-2 text-xs font-semibold text-[#6B7280]">
          {MATCH_LEVEL_LABELS[med.matchLevel]}
          <span className="ml-1 text-[#9CA3AF]">({med.matchLevel}{med.confidenceScore != null ? ` · score ${med.confidenceScore.toFixed(1)}` : ""})</span>
        </p>
      )}

      {med.rxcui && <p className="mt-1 text-xs text-[#9CA3AF]">RxNorm code: {med.rxcui}</p>}

      {med.reviewReason && (
        <p className="mt-1 text-xs font-semibold text-[#B45309]">Flagged: {med.reviewReason.replace(/_/g, " ")}</p>
      )}
    </div>
  );
}

function UploadColumn({ group }: { group: UploadMedicationGroup }) {
  return (
    <div className="min-w-0 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <Icon icon="lucide:file-text" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
        <p className="min-w-0 truncate text-sm font-extrabold text-[#0A2947]">{group.upload.fileName}</p>
      </div>
      {group.medications.length === 0 ? (
        <p className="mt-4 text-xs font-medium text-[#9CA3AF]">No medications found in this upload.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {group.medications.map((med) => (
            <MedicationRow key={med.id} med={med} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Medications sharing an RxCUI across DIFFERENT uploads — only "accept"
 *  band counts as a confirmed match (see cascade.py: review/reject rxcuis
 *  are best-guesses, never trusted downstream), so this deliberately
 *  ignores anything not yet at accept confidence. */
function crossDocumentMatches(groups: UploadMedicationGroup[]) {
  const byRxcui = new Map<string, { upload: UploadMedicationGroup["upload"]; med: MedicationMatch }[]>();
  for (const group of groups) {
    for (const med of group.medications) {
      if (med.confidenceBand !== "accept" || !med.rxcui) continue;
      const entries = byRxcui.get(med.rxcui) ?? [];
      entries.push({ upload: group.upload, med });
      byRxcui.set(med.rxcui, entries);
    }
  }
  return [...byRxcui.entries()]
    .map(([rxcui, entries]) => ({ rxcui, entries }))
    .filter(({ entries }) => new Set(entries.map((e) => e.upload.uploadId)).size >= 2);
}

function SameMedicineCard({ rxcui, entries }: { rxcui: string; entries: { upload: UploadMedicationGroup["upload"]; med: MedicationMatch }[] }) {
  return (
    <div className="rounded-2xl border-2 border-[#16A34A]/30 bg-[#F0FDF4] p-4">
      <div className="flex items-center gap-2">
        <Icon icon="lucide:link-2" className="h-4 w-4 shrink-0 text-[#16A34A]" />
        <p className="text-sm font-extrabold text-[#166534]">Same medicine detected — RxNorm code {rxcui}</p>
      </div>
      <div className="mt-3 space-y-2">
        {entries.map(({ upload, med }) => (
          <p key={med.id} className="text-xs text-[#374151]">
            <span className="font-bold">{upload.fileName}:</span> &ldquo;{med.medicationName}&rdquo;
          </p>
        ))}
      </div>
    </div>
  );
}

export function MedicationMatchingView({
  groups,
  isLoading,
  error,
}: {
  groups: UploadMedicationGroup[];
  isLoading: boolean;
  error: string | null;
}) {
  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="grid gap-5 lg:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
              <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 3 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <EmptyState
          icon={<Icon icon="lucide:wifi-off" className="h-8 w-8 text-[#9CA3AF]" />}
          title="Couldn't load medication matching"
          description={error}
          className="py-24"
        />
      </div>
    );
  }

  if (groups.length < 2) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <EmptyState
          icon={<Icon icon="lucide:pill" className="h-8 w-8 text-[#9CA3AF]" />}
          title="Upload two documents to see this in action"
          description="Once you've uploaded at least two documents, this page shows each one's medications and how the system resolved them — including when two differently-worded entries turn out to be the same medicine."
          className="py-24"
        />
      </div>
    );
  }

  const matches = crossDocumentMatches(groups);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-2">
        <h1 className="text-lg font-extrabold text-[#0A2947]">Medication Matching</h1>
        <p className="mt-1 text-sm text-[#8A8A8A]">
          Your 2 most recently uploaded documents, and how each medication they contain was resolved to a
          standard RxNorm code.
        </p>
      </div>

      {matches.length > 0 && (
        <div className="my-5 space-y-3">
          {matches.map(({ rxcui, entries }) => (
            <SameMedicineCard key={rxcui} rxcui={rxcui} entries={entries} />
          ))}
        </div>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {groups.map((group) => (
          <UploadColumn key={group.upload.uploadId} group={group} />
        ))}
      </div>
    </div>
  );
}
